import React, { useCallback, useEffect, useState } from 'react';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import { makeStyles } from 'tss-react/mui';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import { DateTime } from 'luxon';
const { ORDINAL_DAYS, SELECT_MONTHS } = customFrequencyUtils;

const useStyles = makeStyles()((theme) => ({
  row: {
    alignItems: 'center',
    '&:hover .MuiBox-root': {
      visibility: 'visible',
    },
    'div + div': {
      marginLeft: 16,
      minWidth: 88,
    },
  },
  select: {
    paddingTop: 6,
    paddingLeft: 16,
    '&:focus': {
      borderRadius: 12,
    },
  },
  actionBtnRow: {
    visibility: 'hidden',
    'button + button': {
      marginLeft: 16,
    },
  },
  actionBtn: {
    padding: 8,
    borderRadius: 12,
    background: theme.palette.border.light,
    '& svg': {
      color: `${theme.palette.icon.light} !important`,
    },
  },
  errorText: {
    color: theme.palette.error.main,
    visibility: 'visible',
  },
}));

function DateRow({
  value,
  onChange,
  dueOn,
  onAdd,
  onDelete,
  error,
}: {
  value: string | null;
  onChange: (date: string) => void;
  dueOn: DateTime;
  onAdd?: () => void;
  onDelete?: (removeDate: string) => void;
  error: boolean;
}) {
  const { classes } = useStyles();
  const [month, day] = value?.split('/').map(Number) || [null, null];
  const [date, setDate] = useState({ day, month });

  const createMonthDayString = useCallback(() => {
    if (date.day && date.month && dueOn) {
      if (date.day > 0) {
        return `${date.month}/${date.day}`;
      }

      const day = DateTime.local(dueOn.year, date.month);
      const newDate = day && day.daysInMonth && day.daysInMonth + date.day + 1;
      return `${date.month}/${newDate}`;
    } else {
      return `${dueOn.month}/${dueOn.day}`;
    }
  }, [date.day, date.month, dueOn]);

  useEffect(() => {
    if (date?.month && date?.day) {
      const newDate = createMonthDayString();
      if (newDate !== value) {
        newDate && onChange(newDate);
      }
    }
  }, [date, value, onChange, dueOn.year, dueOn.month, dueOn.day, dueOn, createMonthDayString]);

  // @ts-expect-error FIXME TS7006
  const onChangeValue = (prop, newValue) => {
    const proposedDate = { ...date, [prop]: newValue };
    if (proposedDate.month && proposedDate.day && proposedDate.day > 28) {
      if ([2].includes(proposedDate.month) && proposedDate.day >= 28) {
        proposedDate.day = -1;
      } else if ([4, 6, 9, 11].includes(proposedDate.month) && proposedDate.day > 30) {
        proposedDate.day = 30;
      }
    }

    setDate(proposedDate);
  };

  const onDeleteDate = () => {
    if (onDelete) {
      onDelete(createMonthDayString());
      setDate({ day: null, month: null });
    }
  };

  return (
    <Box className={classes.row} sx={{ width: '100%', my: 1, display: 'flex', flexDirection: 'row', height: 36 }}>
      <Select
        id='days-dropdown'
        variant='filled'
        classes={{ select: classes.select }}
        value={date?.day === 31 ? -1 : date?.day || ''}
        onChange={(e) => onChangeValue('day', e.target.value)}
        sx={{ display: 'flex', flex: 1 }}
        error={error}
      >
        {ORDINAL_DAYS.map((dayItem) => (
          <MenuItem key={dayItem.value} value={dayItem.value}>
            {dayItem.name}
          </MenuItem>
        ))}
      </Select>

      <Select
        id='months-dropdown'
        variant='filled'
        classes={{ select: classes.select }}
        value={date?.month || ''}
        onChange={(e) => onChangeValue('month', e.target.value)}
        sx={{ display: 'flex', flex: 3 }}
        error={error}
      >
        {SELECT_MONTHS.map((monthItem) => (
          <MenuItem key={monthItem.value} value={monthItem.value}>
            {monthItem.label}
          </MenuItem>
        ))}
      </Select>

      <Box className={classes.actionBtnRow}>
        {error && <FormHelperText className={classes.errorText}>Required</FormHelperText>}
        {onDelete && (
          <ButtonBase className={classes.actionBtn} disabled={!(date?.day && date?.month)} onClick={onDeleteDate}>
            <DeleteIcon fontSize='small' />
          </ButtonBase>
        )}
        {onAdd && (
          <ButtonBase className={classes.actionBtn} disabled={!(date?.day && date?.month)} onClick={onAdd}>
            <AddCircleIcon fontSize='small' />
          </ButtonBase>
        )}
      </Box>
    </Box>
  );
}

export default DateRow;
